import { Datum } from '@nivo/line';
import { format as d3Format } from 'd3-format';

import { NULL_VALUE } from './formatters.utils';

export const formatWithD3FormatStr = (value: number, d3FormatStr: string) => {
  return d3Format(d3FormatStr)(value);
};

export interface D3NumberFormatConfig {
  commasForThousands?: boolean;
  precision?: number;
}
export const getD3NumberFormat = (config: D3NumberFormatConfig = {}) => {
  const { commasForThousands = true, precision = 0 } = config;
  return `${commasForThousands ? ',' : ''}${
    precision ? `.${precision}f` : 'd'
  }`;
};
export const formatNumberD3 = (
  value: number | null | undefined,
  config?: D3NumberFormatConfig,
) => {
  if (value == null) {
    return NULL_VALUE;
  }
  const formatStr = getD3NumberFormat(config);
  return formatWithD3FormatStr(value, formatStr);
};

export interface D3CurrencyFormatConfig {
  commasForThousands?: boolean;
  showCents?: boolean;
}
export const getD3MoneyFormat = (config: D3CurrencyFormatConfig = {}) => {
  const { commasForThousands = true, showCents = false } = config;
  return `$${commasForThousands ? ',' : ''}${showCents ? '.2f' : 'd'}`;
};
export const formatMoneyD3 = (
  value: number | null | undefined,
  config?: D3CurrencyFormatConfig,
) => {
  if (value == null) {
    return NULL_VALUE;
  }
  const formatStr = getD3MoneyFormat(config);
  return formatWithD3FormatStr(value, formatStr);
};

export interface D3PercentFormatConfig {
  precision?: number;
  isDecimal?: boolean;
}
export const getD3PercentFormat = (config: D3PercentFormatConfig = {}) => {
  const { precision = 0 } = config;
  return `.${precision}%`;
};
export const formatPercentD3 = (
  value: number | null | undefined,
  config?: D3PercentFormatConfig,
) => {
  if (value == null) {
    return NULL_VALUE;
  }
  const isDecimal = config?.isDecimal !== undefined ? config.isDecimal : true;
  const formatStr = getD3PercentFormat(config);
  // the d3 % format assumes the data is in decimal format, so if it's not, we need to divide by 100
  return formatWithD3FormatStr(isDecimal ? value : value / 100, formatStr);
};

// reads the d3 format string and returns the number of extra characters
// that will be added to the final y-value label. used to help shrink chart
// margins on mobile devices
const d3FormatExtraSpacing = (
  d3FormatStr: string | undefined,
  charLen: number,
) => {
  if (!d3FormatStr || d3FormatStr === 'd') {
    return 0;
  }

  let extraWidth = 0;

  // add a space for the dollar sign
  if (d3FormatStr.includes('$')) {
    extraWidth += 2;
  }

  // add two spaces for the percent sign because it's big and gets cut off
  if (d3FormatStr.includes('%')) {
    extraWidth += 2;
  }

  // add one extra space for each comma. estimating with natural log since
  // there is no built in log3 and we don't need to be perfectly precise
  if (d3FormatStr.includes(',')) {
    extraWidth += Math.floor(charLen / 3) - (charLen % 3 > 0 ? 0 : 1);
  }

  // If '.' is in it, add 1 plus the number immediately to the right of '.'
  // adds 1 extra space for the dot plus one for each degree of precision
  if (d3FormatStr.includes('.')) {
    extraWidth += 1;
    const dotIndex = d3FormatStr.indexOf('.');
    if (d3FormatStr.length > dotIndex + 1) {
      const nextChar = d3FormatStr[dotIndex + 1];
      const precision = nextChar ? parseInt(nextChar, 10) : NaN;
      extraWidth += isNaN(precision) ? 0 : precision;
    }
  }

  return extraWidth;
};

// counts the character length of the longest value in the set of y-values, then
// computes the smallest right-hand margin that still allows the full y-value
// to be visible on the chart without being cut off
export const calcMobileMarginAdjustment = (
  data: readonly Datum[],
  d3FormatStr: string | undefined,
) => {
  let longestLabelCharLen = 0;

  // find the longest y value in each chart
  data.forEach((point: Datum) => {
    const yval = point.y?.valueOf();
    if (!yval) {
      return;
    }
    if (typeof yval === 'string') {
      if (yval.length > longestLabelCharLen) {
        longestLabelCharLen = yval.length;
      }
    } else {
      const numDigits = Math.floor(Math.log10(yval)) + 1;
      if (numDigits > longestLabelCharLen) {
        longestLabelCharLen = numDigits;
      }
    }
  });
  const extraSpace = d3FormatExtraSpacing(d3FormatStr, longestLabelCharLen);
  longestLabelCharLen += extraSpace;

  // return an appropriate margin in pixels
  if (longestLabelCharLen >= 7) {
    return 70;
  }
  if (longestLabelCharLen <= 2) {
    return 25;
  }
  return longestLabelCharLen * 10;
};
